import { RESOURCES, type GameEvent, type Resources } from '../engine';
import type { LogEntry } from './game-client';
import { DEV_CARD_LABELS, RESOURCE_LABELS } from './theme';

export function formatResources(r: Resources): string {
  const parts = RESOURCES.filter((k) => r[k] > 0).map((k) => `${r[k]} ${RESOURCE_LABELS[k].toLowerCase()}`);
  return parts.length ? parts.join(', ') : 'nothing';
}

/** Human-readable log line, or null for events that aren't worth a line. */
export function describeEvent(e: GameEvent, names: readonly string[]): string | null {
  const n = (i: number) => names[i] ?? `Player ${i + 1}`;
  switch (e.type) {
    case 'built':
      return `${n(e.player)} built a ${e.piece}${e.free && e.piece !== 'city' ? ' (free)' : ''}.`;
    case 'setupResources':
      return `${n(e.player)} collected ${formatResources(e.resources)}.`;
    case 'rolled':
      return `${n(e.player)} rolled ${e.dice[0] + e.dice[1]} (${e.dice[0]} + ${e.dice[1]}).`;
    case 'sevenRerolled':
      return `${n(e.player)} rolled 7 before the robber is in play, so rolled again.`;
    case 'produced': {
      const lines = e.gains.flatMap((g, i) => (RESOURCES.some((r) => g[r] > 0) ? [`${n(i)} got ${formatResources(g)}`] : []));
      return lines.length ? `${lines.join('; ')}.` : 'Nobody collected anything.';
    }
    case 'shortage':
      return `The bank ran short of ${RESOURCE_LABELS[e.resource].toLowerCase()}.`;
    case 'mustDiscard': {
      const who = e.owed.flatMap((c, i) => (c > 0 ? [`${n(i)} (${c})`] : []));
      return `Must discard: ${who.join(', ')}.`;
    }
    case 'discarded':
      return `${n(e.player)} discarded ${formatResources(e.resources)}.`;
    case 'robberMoved':
      return `${n(e.player)} moved the robber.`;
    case 'stole':
      return `${n(e.thief)} stole ${e.resource ? `1 ${RESOURCE_LABELS[e.resource].toLowerCase()}` : 'a card'} from ${n(e.victim)}.`;
    case 'boughtDevCard':
      if (e.card === 'victoryPoint') return `${n(e.player)} bought a Victory Point card (+1 point).`;
      return `${n(e.player)} bought a development card${e.card ? ` (${DEV_CARD_LABELS[e.card]})` : ''}.`;
    case 'playedDevCard':
      return `${n(e.player)} played ${DEV_CARD_LABELS[e.card]}.`;
    case 'yearOfPlenty':
      return `${n(e.player)} took ${e.resources.map((r) => RESOURCE_LABELS[r].toLowerCase()).join(' and ')} from the bank.`;
    case 'monopoly':
      return `${n(e.player)} took ${e.taken} ${RESOURCE_LABELS[e.resource].toLowerCase()}.`;
    case 'bankTrade':
      return `${n(e.player)} traded ${e.giveCount} ${RESOURCE_LABELS[e.give].toLowerCase()} for 1 ${RESOURCE_LABELS[e.receive].toLowerCase()}.`;
    case 'tradeOffered':
      return `${n(e.trade.from)} offers ${formatResources(e.trade.give)} for ${formatResources(e.trade.want)}.`;
    case 'tradeResponded':
      if (e.response.kind === 'counter') {
        return `${n(e.player)} counters: ${formatResources(e.response.give)} for ${formatResources(e.response.want)}.`;
      }
      return `${n(e.player)} ${e.response.kind === 'accept' ? 'accepted' : 'declined'}.`;
    case 'tradeCompleted':
      return `${n(e.from)} traded ${formatResources(e.fromGave)} to ${n(e.to)} for ${formatResources(e.toGave)}.`;
    case 'tradeCancelled':
      return null;
    case 'longestRoad':
      return e.player === null ? 'Longest Road is unclaimed.' : `${n(e.player)} has the Longest Road.`;
    case 'largestArmy':
      return `${n(e.player)} has the Largest Army.`;
    case 'turnStarted':
      return `Turn ${e.turn} · ${n(e.player)}`;
    case 'gameContinued':
      return `Play continues to ${e.target} points.`;
    case 'buildRequested':
      return e.on ? `${n(e.player)} will build after this turn.` : `${n(e.player)} won't build after this turn after all.`;
    case 'specialBuild':
      return `${n(e.player)} builds between turns.`;
    case 'firstRoll': {
      const rounds = e.rounds.map((round) => round.map((r) => `${n(r.player)} ${r.dice[0] + r.dice[1]}`).join(', '));
      const ties = rounds.length > 1 ? ` (tie, rolled again: ${rounds.slice(1).join('; then ')})` : '';
      return `Rolled for first: ${rounds[0]}${ties}. ${n(e.first)} goes first.`;
    }
    case 'gameOver':
      return `${n(e.winner)} wins!`;
  }
}

/** Whose event this is, for the log's colour dot. */
export function eventSeat(e: GameEvent): number | undefined {
  switch (e.type) {
    case 'stole':
      return e.thief;
    case 'tradeOffered':
      return e.trade.from;
    case 'tradeCompleted':
      return e.from;
    case 'gameOver':
      return e.winner;
    case 'firstRoll':
      return e.first;
    case 'longestRoad':
      return e.player ?? undefined;
    case 'produced':
    case 'mustDiscard':
    case 'shortage':
    case 'tradeCancelled':
    case 'gameContinued':
      return undefined;
    default:
      return e.player;
  }
}

/** A log line for an event (null if it isn't worth one). */
export function logEntry(e: GameEvent, names: readonly string[], id: number): LogEntry | null {
  const text = describeEvent(e, names);
  if (!text) return null;
  const seat = eventSeat(e);
  return { id, text, ...(seat !== undefined ? { seat } : {}), ...(e.type === 'turnStarted' ? { divider: true } : {}) };
}
